// import * as React from "react";
import React, { useState, useCallback, useRef } from "react";

import { Formik, Form } from "formik";

import { useHistory } from "react-router-dom";

import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { FormikInitialValuesNewRequest } from "../model/form.model";

import { initialValuesNewRequest } from "../state/initial";

import { useNewRequestController } from "./hook/useNewRequestController";

import { PurchaseStep } from "./steps/purchase";
import { ObservationStep } from "./steps/observation";
import { ProductsStep } from "./steps/products";
import { ReviewStep } from "./steps/review";

import { MobileWizard } from "./MobileWizard";

import { ConfirmSubmitDialog } from "./components/ConfirmSubmitDialog";
import { OrderResultDialog } from "./components/orderResultDialog/index";

import ROUTES from "@config/routes";
import { LocalStorageKeysOrders } from "@pages/User/MyOrders/utilsOrders/localStorageOrders";

import { convertDateInFormatZ } from "@utils/index";
import { unmaskCnpj } from "@utils/fiscal/cnpj/cnpj.helpers";
import { formatBRL } from "@utils/currency";
import { isDevelopment } from "@config/appConfig";
import { paymentLabel } from "./hook/useReviewStep";
import { formatQty } from "./hook/useProductsStep";

export function NewRequestPage() {
  const isBlocked = !isDevelopment;

  const theme = useTheme();

  const history = useHistory();

  const vm = useNewRequestController();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [mobileWizardKey, setMobileWizardKey] = useState(0);

  const pendingRef = useRef<FormikInitialValuesNewRequest | null>(null);

  const requestConfirm = useCallback(() => {
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Box
      className={"container-NewRequestPage"}
      sx={{ py: 1, mx: { xs: 1, lg: 3 } }}
    >
      <Box
        sx={{
          mb: 1,
          position: "relative",
        }}
      >
        <Tooltip title="Esta é a versão V2 do pedido, com melhorias em performance e usabilidade em relação à versão anterior.">
          <Chip
            label="Nova versão • V2"
            color="primary"
            size="small"
            variant="outlined"
          />
        </Tooltip>{" "}
        <br />
        <Typography
          variant="caption"
          color="error"
          sx={{ mt: 0.5, fontStyle: "italic" }}
        >
          Disponível apenas em ambiente de desenvolvimento e localhost
        </Typography>
      </Box>
      <Formik<FormikInitialValuesNewRequest>
        initialValues={initialValuesNewRequest}
        onSubmit={(values, actions) => {
          try {
            pendingRef.current = values;
            requestConfirm();
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <div
            style={{
              opacity: isBlocked ? 0.4 : 1,
              pointerEvents: isBlocked ? "none" : "auto",
              filter: isBlocked ? "grayscale(1)" : "none",
              transition: "opacity 0.2s ease",
            }}
          >
            <Form>
              {isMobile ? (
                <MobileWizard key={mobileWizardKey} />
              ) : (
                <Stack spacing={2.5}>
                  <PurchaseStep />
                  <ObservationStep />
                  <ProductsStep />
                  <ReviewStep />
                </Stack>
              )}
            </Form>

            {/* Modal de Confirmação antes do envio */}
            <ConfirmSubmitDialog
              open={open}
              description={
                <>
                  <Box sx={{ mb: 1 }}>
                    <strong>Revise antes de enviar</strong>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ mb: 2 }}
                  >
                    Itens no carrinho: <strong>{vm.state.cart.length}</strong>
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    Ao confirmar, o pedido será enviado e não poderá ser
                    alterado.
                  </Box>

                  {vm.state.cart.length > 0 && (
                    <Table size="small" sx={{ "& th": { fontWeight: 600 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Produto</TableCell>
                          <TableCell align="right">Quantidade</TableCell>
                          <TableCell align="right">Subtotal</TableCell>
                          <TableCell align="center">Prazo</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vm.state.cart.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.description}</TableCell>
                            <TableCell align="right">
                              {formatQty(item.quantity)}
                            </TableCell>
                            <TableCell align="right">
                              {formatBRL(item.subtotal, { fractionDigits: 2 })}
                            </TableCell>
                            <TableCell align="center">
                              {paymentLabel(item.payment, item.deadlinePayment)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </>
              }
              loading={formik.isSubmitting ?? loadingSubmit}
              onCancel={() => {
                pendingRef.current = null;
                close();
              }}
              onConfirm={async () => {
                setLoadingSubmit(true);
                const pending = pendingRef.current;
                if (!pending) {
                  close();
                  return;
                }

                formik.setSubmitting(true);
                try {
                  await vm.action.submitOrder(pending);
                } finally {
                  formik.setSubmitting(false);
                  pendingRef.current = null;
                  setLoadingSubmit(false);
                  close();
                }
              }}
            />
            {/* Modal de Resultado (Sucesso/Erro/Parcial) */}
            <OrderResultDialog
              result={vm.state.createResult}
              cart={vm.state.cart}
              onClose={() => {
                vm.action.clearCreateResult();
                vm.action.resetFlow(formik);
                if (isMobile) setMobileWizardKey((k) => k + 1);
              }}
              onNewOrder={() => {
                vm.action.resetFlow(formik);
                if (isMobile) setMobileWizardKey((k) => k + 1);
              }}
              onConsult={(orderCode) => {
                // navegar para o modulo "seus-pedidos"
                localStorage.setItem(
                  LocalStorageKeysOrders.DateDelivery,
                  convertDateInFormatZ(formik.values.purchase.deliveryDate)
                );

                localStorage.setItem(
                  LocalStorageKeysOrders.CNPJFromNewOrder,
                  unmaskCnpj(formik.values.purchase.customerCnpj)
                );

                localStorage.setItem(
                  LocalStorageKeysOrders.isOrderFromNewRequest,
                  "true"
                );
                history.push(ROUTES.USER_ROUTES.MY_ORDERS);
              }}
              resolveBranchName={() => {
                return formik.values.purchase?.branchName ?? "-";
              }}
              resolveProductName={(productId) => {
                const product = vm.state.cart.find(
                  (c) => c.productId === productId
                );
                return product
                  ? `${product.productId} - ${product.description}`
                  : `${productId}`;
              }}
            />
          </div>
        )}
      </Formik>
    </Box>
  );
}
